const FRAME_MAX_PX = 720

const once = (el: HTMLVideoElement, ok: string, ms: number) =>
  new Promise<boolean>((resolve) => {
    const done = (v: boolean) => {
      clearTimeout(timer)
      el.removeEventListener(ok, yes)
      el.removeEventListener('error', no)
      resolve(v)
    }
    const yes = () => done(true)
    const no = () => done(false)
    const timer = setTimeout(no, ms)
    el.addEventListener(ok, yes)
    el.addEventListener('error', no)
  })

function grab(el: HTMLVideoElement) {
  const k = Math.min(1, FRAME_MAX_PX / Math.max(el.videoWidth, el.videoHeight))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(el.videoWidth * k)
  canvas.height = Math.round(el.videoHeight * k)
  canvas.getContext('2d')!.drawImage(el, 0, 0, canvas.width, canvas.height)
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82))
}

/**
 * A picked clip's length plus JPEG stills: the poster (first frame) and frames
 * at about 1/3 and 2/3, which screening checks. Null if the browser can't read it.
 */
export async function readStoryClip(
  file: File,
): Promise<{ duration: number; poster: Blob; frames: Blob[] } | null> {
  const url = URL.createObjectURL(file)
  const el = document.createElement('video')
  el.muted = true
  el.playsInline = true
  el.preload = 'auto'
  try {
    const meta = once(el, 'loadedmetadata', 8000)
    el.src = url
    if (!(await meta)) return null
    const duration = el.duration
    const stills: Blob[] = []
    const span = Number.isFinite(duration) && duration > 0 ? duration : 0
    // iOS Safari only decodes a frame after a seek, hence 0.001 rather than 0.
    for (const at of [0.001, span / 3, (span * 2) / 3]) {
      const seeked = once(el, 'seeked', 4000)
      el.currentTime = at
      if (!(await seeked) || !el.videoWidth) return null
      const still = await grab(el)
      if (!still) return null
      stills.push(still)
    }
    return { duration, poster: stills[0], frames: stills.slice(1) }
  } finally {
    el.removeAttribute('src')
    el.load()
    URL.revokeObjectURL(url)
  }
}

export const STORY_PHOTO_MAX_PX = 1440
export const STORY_PHOTO_QUALITY = 0.86

/** A photo as a JPEG no larger than `max` px on its long side. */
export async function downscaleImage(file: File, max = STORY_PHOTO_MAX_PX): Promise<Blob> {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = reject
      el.src = url
    })
    const k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(img.naturalWidth * k)
    canvas.height = Math.round(img.naturalHeight * k)
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height)
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('encode'))),
        'image/jpeg',
        STORY_PHOTO_QUALITY,
      ),
    )
  } finally {
    URL.revokeObjectURL(url)
  }
}
